<template>
  <div class="refmat-container">
    <v-tabs v-model="subTab" class="mb-4" color="orange-darken-1">
      <v-tab value="scan">Erfassung</v-tab>
      <v-tab value="archiv">Schein-Archiv</v-tab>
    </v-tabs>

    <!-- Erfassung -->
    <v-card v-if="subTab === 'scan'" class="status-overview-card">
      <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-2">
        <span>Artikelscan</span>
        <div class="d-flex ga-2 flex-wrap">
          <v-btn
            color="primary"
            variant="outlined"
            :disabled="positionen.length === 0"
            :loading="loading.printing"
            @click="printSchein"
          >
            <v-icon start>mdi-printer</v-icon>
            Palettenschein drucken
          </v-btn>
          <v-btn
            color="error"
            variant="outlined"
            :disabled="positionen.length === 0"
            @click="dialogs.clearConfirm = true"
          >
            <v-icon start>mdi-delete-sweep</v-icon>
            Liste leeren
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <div class="info-box mb-4">
          <div class="d-flex justify-space-between flex-wrap ga-4">
            <div>
              <div class="info-label">Artikel</div>
              <div class="info-value">{{ statistik.artikel }}</div>
            </div>
            <div>
              <div class="info-label">Stück gesamt</div>
              <div class="info-value">{{ statistik.gesamt }}</div>
            </div>
          </div>
        </div>

        <v-row class="mb-4">
          <v-col cols="12" md="8">
            <v-text-field
              ref="scannerInput"
              v-model="scanInput"
              label="EAN / CMMF scannen"
              placeholder="Barcode hier einscannen..."
              autofocus
              density="comfortable"
              hide-details
              @keyup.enter="handleScan"
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-text-field
              v-model.number="scanMenge"
              label="Menge"
              type="number"
              min="1"
              density="comfortable"
              hide-details
            />
          </v-col>
          <v-col cols="6" md="2">
            <v-btn color="orange-darken-1" block height="48" :loading="loading.scanning" @click="handleScan">
              Scannen
            </v-btn>
          </v-col>
        </v-row>

        <v-alert
          v-if="scanFeedback.show"
          :type="scanFeedback.type"
          closable
          class="mb-4"
          @click:close="scanFeedback.show = false"
        >
          {{ scanFeedback.message }}
        </v-alert>

        <v-data-table
          :headers="tableHeaders"
          :items="positionen"
          :loading="loading.positionen"
          density="compact"
        >
          <template #item.menge="{ item }">
            <span class="font-weight-medium">{{ item.menge }}</span>
          </template>
          <template #item.actions="{ item }">
            <v-btn icon size="small" color="error" @click="removePosition(item.id)">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Archiv -->
    <v-card v-else class="status-overview-card">
      <v-card-title class="d-flex justify-space-between align-center">
        <span>Palettenschein-Archiv</span>
        <v-btn variant="tonal" :loading="loading.archiv" @click="fetchArchiv">
          <v-icon start>mdi-refresh</v-icon>
          Aktualisieren
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          :headers="archivHeaders"
          :items="archivScheine"
          :loading="loading.archiv"
          density="compact"
        >
          <template #item.gedruckt_am="{ item }">
            {{ formatDateTime(item.gedruckt_am) }}
          </template>
          <template #item.actions="{ item }">
            <v-btn size="small" color="primary" variant="tonal" @click="reprintSchein(item.id)">
              <v-icon start>mdi-printer</v-icon>
              Nachdruck
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialogs.eanNotFound" max-width="420">
      <v-card>
        <v-card-title>Artikel nicht gefunden</v-card-title>
        <v-card-text>
          Der Code <code>{{ notFoundEan }}</code> ist in der DACH Material-Liste nicht vorhanden.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="closeNotFound">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.clearAfterPrint" max-width="460" persistent>
      <v-card>
        <v-card-title>Scan-Liste leeren?</v-card-title>
        <v-card-text>
          Schein wurde archiviert. Soll die aktuelle Scan-Liste gelöscht werden?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogs.clearAfterPrint = false">Behalten</v-btn>
          <v-btn color="error" :loading="loading.clearing" @click="clearPositionen">
            Löschen &amp; neu starten
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogs.clearConfirm" max-width="420" persistent>
      <v-card>
        <v-card-title>Liste wirklich leeren?</v-card-title>
        <v-card-text>
          Alle {{ positionen.length }} Positionen werden gelöscht.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogs.clearConfirm = false">Abbrechen</v-btn>
          <v-btn color="error" :loading="loading.clearing" @click="clearPositionen">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
/**
 * Refurbish Artikelscan (DACH-Liste) + Palettenschein-Archiv.
 */
import { ref, watch, nextTick, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const toast = useToast();
const apiUrl = () => 'refurbish_artikelscan/index.php';
const client = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const subTab = ref('scan');
const scannerInput = ref(null);
const scanInput = ref('');
const scanMenge = ref(1);
const positionen = ref([]);
const statistik = ref({ artikel: 0, gesamt: 0 });
const notFoundEan = ref('');
const archivScheine = ref([]);
const scanFeedback = ref({ show: false, type: 'success', message: '' });

const loading = ref({
  positionen: false,
  scanning: false,
  printing: false,
  clearing: false,
  archiv: false,
});

const dialogs = ref({
  eanNotFound: false,
  clearAfterPrint: false,
  clearConfirm: false,
});

const tableHeaders = [
  { title: 'Consumer Brand', key: 'consumer_brand', sortable: true },
  { title: 'Bezeichnung', key: 'bezeichnung', sortable: true },
  { title: 'Commercial Ref', key: 'commercial_ref', sortable: true },
  { title: 'CMMF', key: 'cmmf_nummer', sortable: true },
  { title: 'EAN', key: 'ean', sortable: true },
  { title: 'Anzahl', key: 'menge', sortable: true, align: 'center' },
  { title: '', key: 'actions', sortable: false, align: 'end' },
];

const archivHeaders = [
  { title: 'Schein-Nr.', key: 'schein_nr', sortable: true },
  { title: 'Erfasst von', key: 'user_name', sortable: true },
  { title: 'Artikel', key: 'anzahl_positionen', sortable: true },
  { title: 'Stück', key: 'gesamt_menge', sortable: true },
  { title: 'Gedruckt am', key: 'gedruckt_am', sortable: true },
  { title: '', key: 'actions', sortable: false, align: 'end' },
];

const showScanFeedback = (type, message) => {
  scanFeedback.value = { show: true, type, message };
};

const applyListResponse = (data) => {
  if (Array.isArray(data.positionen)) {
    positionen.value = data.positionen;
  }
  if (data.statistik) {
    statistik.value = data.statistik;
  }
};

const focusScanner = () => {
  nextTick(() => scannerInput.value?.focus?.());
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('de-DE');
};

const fetchPositionen = async () => {
  loading.value.positionen = true;
  try {
    const response = await client.get(apiUrl(), { params: { action: 'get_positionen' } });
    if (response.data?.status === 'success') {
      applyListResponse(response.data);
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Laden der Scan-Liste');
  } finally {
    loading.value.positionen = false;
  }
};

const fetchArchiv = async () => {
  loading.value.archiv = true;
  try {
    const response = await client.get(apiUrl(), { params: { action: 'get_archiv', limit: 100 } });
    if (response.data?.status === 'success') {
      archivScheine.value = response.data.scheine || [];
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Laden des Archivs');
  } finally {
    loading.value.archiv = false;
  }
};

const handleScan = async () => {
  const code = scanInput.value.trim();
  if (!code) {
    showScanFeedback('warning', 'Bitte einen Code eingeben');
    return;
  }

  loading.value.scanning = true;
  try {
    const response = await client.get(apiUrl(), {
      params: { action: 'search_ean', ean: code },
    });

    if (response.data?.status === 'success' && response.data.artikel) {
      const artikel = response.data.artikel;
      const saveResp = await client.post(apiUrl(), {
        action: 'scan_article',
        material_id: artikel.id,
        menge: scanMenge.value || 1,
        ean: code,
      });
      if (saveResp.data?.status === 'success') {
        applyListResponse(saveResp.data);
        const hint =
          Number(response.data.treffer_anzahl || 1) > 1
            ? ` (erster von ${response.data.treffer_anzahl} EAN-Treffern)`
            : '';
        showScanFeedback(
          'success',
          `Erfasst: ${artikel.kurztext || artikel.cmmf_nummer}${hint}`
        );
        scanInput.value = '';
        scanMenge.value = 1;
        focusScanner();
      } else {
        toast.error(saveResp.data?.message || 'Erfassen fehlgeschlagen');
      }
    } else {
      notFoundEan.value = code;
      scanInput.value = '';
      scanMenge.value = 1;
      dialogs.value.eanNotFound = true;
    }
  } catch (error) {
    console.error(error);
    const apiMsg = error?.response?.data?.message;
    showScanFeedback('error', apiMsg ? `Fehler: ${apiMsg}` : 'Fehler beim Scannen');
  } finally {
    loading.value.scanning = false;
  }
};

const closeNotFound = () => {
  dialogs.value.eanNotFound = false;
  notFoundEan.value = '';
  focusScanner();
};

const removePosition = async (positionId) => {
  try {
    const response = await client.post(apiUrl(), {
      action: 'remove_position',
      position_id: positionId,
    });
    if (response.data?.status === 'success') {
      applyListResponse(response.data);
      toast.success('Position gelöscht');
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Löschen');
  }
};

const clearPositionen = async () => {
  loading.value.clearing = true;
  try {
    const response = await client.post(apiUrl(), { action: 'clear_positionen' });
    if (response.data?.status === 'success') {
      applyListResponse(response.data);
      dialogs.value.clearAfterPrint = false;
      dialogs.value.clearConfirm = false;
      toast.success('Scan-Liste geleert');
      focusScanner();
    }
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Leeren');
  } finally {
    loading.value.clearing = false;
  }
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * Baut den Palettenschein-HTML (Commercial Ref / CMMF / Anzahl).
 */
const buildScheinHtml = (opts) => {
  const {
    scheinNr = '',
    datum = new Date().toLocaleString('de-DE'),
    items = [],
  } = opts;

  let sum = 0;
  const rows = items
    .map((p) => {
      const m = Number(p.menge || 0);
      sum += m;
      return (
        `<tr>` +
        `<td>${escapeHtml(p.cmmf_nummer || '')}</td>` +
        `<td>${escapeHtml(p.commercial_ref || '')}</td>` +
        `<td style="text-align:center;">${m}</td>` +
        `</tr>`
      );
    })
    .join('');

  const totalsRow =
    `<tr class="totals">` +
    `<td colspan="2"><strong>GESAMT</strong></td>` +
    `<td style="text-align:center;"><strong>${sum}</strong></td>` +
    `</tr>`;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Palettenschein ${escapeHtml(scheinNr)}</title>
  <style>
    @page { size: A4; margin: 12mm; }
    body { font-family: Arial, sans-serif; font-size: 11pt; color: #000; margin: 0; }
    .header { border-bottom: 2px solid #000; padding-bottom: 4mm; margin-bottom: 6mm; text-align: center; }
    .header h1 { margin: 0; font-size: 18pt; }
    .meta { margin-top: 2mm; font-size: 10pt; color: #333; }
    table.pos { width: 100%; border-collapse: collapse; }
    table.pos th, table.pos td { border: 1px solid #000; padding: 2mm 2.5mm; font-size: 10pt; }
    table.pos th { background: #e8e8e8; }
    tr.totals td { background: #f0f0f0; }
  </style>
</head>
<body>
  <div class="header">
    <h1>PALETTENSCHEIN</h1>
    <div class="meta">
      ${scheinNr ? `Schein-Nr.: <strong>${escapeHtml(scheinNr)}</strong> · ` : ''}
      ${escapeHtml(datum)}
    </div>
  </div>
  <table class="pos">
    <thead>
      <tr>
        <th>CMMF</th>
        <th>Commercial Ref</th>
        <th>Anzahl</th>
      </tr>
    </thead>
    <tbody>
      ${rows || '<tr><td colspan="3" style="text-align:center;color:#888;">Keine Positionen</td></tr>'}
      ${totalsRow}
    </tbody>
  </table>
</body>
</html>`;
};

const openPrintWindow = (html) => {
  const win = window.open('', '_blank', 'width=900,height=900');
  if (!win) {
    toast.error('Popup blockiert – Drucken nicht möglich');
    return false;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => {
    try {
      win.print();
    } catch (e) {
      console.warn(e);
    }
  }, 350);
  return true;
};

const printSchein = async () => {
  if (positionen.value.length === 0) {
    toast.warning('Keine Positionen zum Drucken');
    return;
  }
  loading.value.printing = true;
  try {
    const response = await client.post(apiUrl(), { action: 'save_schein' });
    if (response.data?.status !== 'success') {
      toast.error(response.data?.message || 'Schein konnte nicht gespeichert werden');
      return;
    }

    const html = buildScheinHtml({
      scheinNr: response.data.schein_nr || '',
      datum: response.data.gedruckt_am
        ? formatDateTime(response.data.gedruckt_am)
        : new Date().toLocaleString('de-DE'),
      items: response.data.positionen || [],
    });

    if (openPrintWindow(html)) {
      toast.success(`Schein ${response.data.schein_nr} archiviert`);
      dialogs.value.clearAfterPrint = true;
      await fetchArchiv();
    }
  } catch (error) {
    console.error(error);
    const apiMsg = error?.response?.data?.message;
    toast.error(apiMsg || 'Fehler beim Drucken');
  } finally {
    loading.value.printing = false;
  }
};

const reprintSchein = async (scheinId) => {
  try {
    const response = await client.get(apiUrl(), {
      params: { action: 'get_schein', schein_id: scheinId },
    });
    if (response.data?.status !== 'success') {
      toast.error(response.data?.message || 'Schein nicht gefunden');
      return;
    }
    const schein = response.data.schein;
    const html = buildScheinHtml({
      scheinNr: schein.schein_nr || '',
      datum: formatDateTime(schein.gedruckt_am),
      items: schein.positionen || [],
    });
    openPrintWindow(html);
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Nachdruck');
  }
};

watch(subTab, (val) => {
  if (val === 'archiv') {
    fetchArchiv();
  } else {
    focusScanner();
  }
});

onMounted(() => {
  fetchPositionen();
  focusScanner();
});
</script>

<style scoped>
.refmat-container {
  padding: 0;
}

.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

.info-box {
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 8px;
}

.info-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

code {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}
</style>
